import type { CMSType, EntityReference } from "@firecms/core";

export type LevelState = "not_started" | "in_progress" | "completed";

export type UnitState = "not_started" | "in_progress" | "completed";

export type UnitProgress = {
  unitId: string;
  unitCode?: string;
  state: UnitState;
  unlockedAt?: Date;
  startedAt?: Date;
  completedAt?: Date;
};

export type LevelProgress = {
  levelId: string;
  levelCode?: string;
  unitId: string;
  unitCode?: string;
  state: LevelState;
  startedAt?: Date;
  completedAt?: Date;
  /** Stars earned for this activity run (0–3). */
  stars?: number;
  /** Activity score for this run (not event ranking score). */
  score?: number;
  /** Multiplayer role chosen at level start. */
  role?: "leader" | "collaborator";
};

/** Matches `firebase/functions/src/types/actions.ts` ActionType values. */
export const ActionType = {
  LEVEL_ENTERED: "level.entered",
  LEVEL_EXITED: "level.exited",
  QUESTION_ANSWERED: "question.answered",
  UNIT_ACCESS_UNLOCKED: "unit.access_unlocked",
  EVENT_CONTENT_DOWNLOADED: "event.content_downloaded",
  EVENT_JOINED: "event.joined",
  EVENT_LEFT: "event.left",
} as const;

export type ActionType = (typeof ActionType)[keyof typeof ActionType];

/** Type-specific fields; identity is string ids/codes (see collection schema). */
export type ActionLogPayload = Record<string, CMSType>;

export type ActionLogEntry = {
  accountRef: EntityReference;
  eventRef: EntityReference;
  participantRef: EntityReference;
  actionAt: Date;
  writtenAt: Date;
  actionType: ActionType;
  payload: ActionLogPayload;
};

/** UI fields only — account/event/participant refs are implied by the doc path. */
export type ActionLogCollectionEntry = Pick<
  ActionLogEntry,
  "actionAt" | "writtenAt" | "actionType" | "payload"
>;

export type UnitLevelCatalogEntry = {
  levelKey: string;
  order?: number;
  levelId: string;
  levelCode: string;
  modality: string;
  storyTitle?: string;
};

export type UnitProgressSection = {
  unitId: string;
  unitCode: string;
  unitName: string;
  levels: Array<
    UnitLevelCatalogEntry & {
      state: LevelState;
    }
  >;
};

export type ParticipantSummary = {
  uid: string;
  label: string;
};

export type ParticipantActionLogRow = {
  id: string;
  actionAt: Date;
  actionType: string;
  levelCode?: string;
  unitCode?: string;
};
